import { Box, Checkbox, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

// The scope options and the lot list are ONE bounded section: rendered apart they read as
// unrelated controls.
const SCOPE_BOX_SX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
};

const OPTION_SX = {
  m: 0,
  width: '100%',
  alignItems: 'flex-start',
  px: 1.5,
  py: 1.25,
  borderBottom: '1px solid',
  borderColor: 'divider',
  '&:last-of-type': { borderBottom: 'none' },
};

const SELECTED_OPTION_SX = { ...OPTION_SX, bgcolor: colors.inventoryLockOrangeLight };

const LOT_WRAP_SX = {
  bgcolor: colors.inventoryLockOrangeLight,
  borderTop: '1px dashed',
  borderColor: colors.inventoryLockOrangeBorder,
  pl: 5.25,
  pr: 1.5,
  pt: 0.5,
  pb: 1.5,
};

const LOT_LIST_SX = {
  border: '1px solid',
  borderColor: colors.inventoryLockOrangeBorder,
  borderRadius: 1,
  bgcolor: 'background.paper',
  overflow: 'hidden',
  maxHeight: 240,
  overflowY: 'auto',
};

const LOT_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.25,
  py: 0.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  '&:last-of-type': { borderBottom: 'none' },
};

const COL_HEADER_SX = {
  fontWeight: 600,
  fontSize: 11,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const OptionLabel = ({ title, detail, accent }) => (
  <Box sx={{ py: 0.25 }}>
    <Typography variant='body2' sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {/* Body size, not a caption — this count is what the decision turns on. */}
    <Typography
      variant='body2'
      sx={{
        mt: 0.25,
        fontWeight: accent ? 700 : 400,
        color: accent ? colors.inventoryLockOrange : 'text.secondary',
      }}
    >
      {detail}
    </Typography>
  </Box>
);
OptionLabel.propTypes = {
  title: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  accent: PropTypes.bool,
};
OptionLabel.defaultProps = { accent: false };

const RecallLotSelector = ({
  lotMode,
  reservedLots,
  selectedLotIds,
  onLotModeChange,
  onToggleLot,
  onToggleAll,
}) => {
  const allChecked = reservedLots.length > 0 && selectedLotIds.length === reservedLots.length;
  const someChecked = selectedLotIds.length > 0 && !allChecked;
  const totalUnits = reservedLots.reduce((sum, lot) => sum + Number(lot.wipQty ?? 0), 0);
  const allLotsDetail = `${reservedLots.length} ${
    reservedLots.length === 1 ? 'lot' : 'lots'
  } · ${totalUnits} units`;

  return (
    <Box sx={SCOPE_BOX_SX}>
      <RadioGroup value={lotMode} onChange={(event) => onLotModeChange(event.target.value)}>
        <FormControlLabel
          value='all'
          control={<Radio size='small' sx={{ py: 0.25 }} />}
          sx={lotMode === 'all' ? SELECTED_OPTION_SX : OPTION_SX}
          label={
            <OptionLabel title='All reserved lots at this location' detail={allLotsDetail} accent />
          }
        />
        <FormControlLabel
          value='selected'
          control={<Radio size='small' sx={{ py: 0.25 }} />}
          sx={lotMode === 'selected' ? SELECTED_OPTION_SX : OPTION_SX}
          label={<OptionLabel title='Selected lots' detail='Choose which lots to return' />}
        />
      </RadioGroup>

      {lotMode === 'selected' && (
        <Box sx={LOT_WRAP_SX}>
          <Box sx={LOT_LIST_SX}>
            {/* Header doubles as select-all. The column labels matter: an unlabelled date could
                as easily be when the lot was received. */}
            <Box sx={{ ...LOT_ROW_SX, bgcolor: 'action.hover', py: 0.25 }}>
              <Checkbox
                size='small'
                checked={allChecked}
                indeterminate={someChecked}
                onChange={(event) => onToggleAll(event.target.checked)}
                inputProps={{ 'aria-label': 'Select all lots' }}
              />
              <Typography sx={{ ...COL_HEADER_SX, flex: 1 }}>Lot #</Typography>
              <Typography sx={COL_HEADER_SX}>Expiration</Typography>
              <Typography sx={{ ...COL_HEADER_SX, minWidth: 92, textAlign: 'right' }}>
                Reserved
              </Typography>
            </Box>

            {reservedLots.map((lot) => (
              <Box
                key={lot.inventoryLotId}
                data-testid={`recall-lot-${lot.inventoryLotId}`}
                sx={LOT_ROW_SX}
              >
                <Checkbox
                  size='small'
                  checked={selectedLotIds.includes(lot.inventoryLotId)}
                  onChange={() => onToggleLot(lot.inventoryLotId)}
                  inputProps={{ 'aria-label': `Select lot ${lot.lotNumber}` }}
                />
                <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }}>
                  {lot.lotNumber}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {lot.expirationDate ? format(new Date(lot.expirationDate), 'MM/dd/yyyy') : '—'}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 700,
                    color: colors.inventoryLockOrange,
                    minWidth: 92,
                    textAlign: 'right',
                  }}
                >
                  {`${lot.wipQty} reserved`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

RecallLotSelector.propTypes = {
  lotMode: PropTypes.oneOf(['all', 'selected']).isRequired,
  reservedLots: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedLotIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLotModeChange: PropTypes.func.isRequired,
  onToggleLot: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
};

export default RecallLotSelector;
